"use client";

import { Tabs as TabsPrimitive } from "@base-ui/react/tabs";
import * as React from "react";

import { cn } from "../lib/utils";

function SegmentedControl({ className, ...props }: TabsPrimitive.Root.Props) {
  return (
    <TabsPrimitive.Root
      data-slot="segmented-control"
      className={cn("flex flex-col gap-2", className)}
      {...props}
    />
  );
}

function SegmentedControlList({
  className,
  children,
  ...props
}: TabsPrimitive.List.Props) {
  const listRef = React.useRef<HTMLDivElement>(null);
  const indicatorRef = React.useRef<HTMLDivElement>(null);
  // Avoid playing the slide animation on first paint — only after a real
  // active-tab change does the user expect the liquid slide.
  const hasAnimatedRef = React.useRef(false);

  const updateIndicator = React.useCallback(() => {
    const list = listRef.current;
    const indicator = indicatorRef.current;
    if (!list || !indicator) return;
    const active = list.querySelector<HTMLElement>("[data-active]");
    if (!active) {
      indicator.style.opacity = "0";
      return;
    }
    const left = Math.round(active.offsetLeft);
    const top = Math.round(active.offsetTop);
    const width = Math.round(active.offsetWidth);
    const height = Math.round(active.offsetHeight);
    if (width === 0 || height === 0) {
      indicator.style.opacity = "0";
      return;
    }

    if (!hasAnimatedRef.current) {
      const prev = indicator.style.transition;
      indicator.style.transition = "none";
      indicator.style.transform = `translate3d(${left}px, ${top}px, 0)`;
      indicator.style.width = `${width}px`;
      indicator.style.height = `${height}px`;
      indicator.style.opacity = "1";
      // Force layout flush before re-enabling transitions.
      void indicator.offsetWidth;
      indicator.style.transition = prev;
      hasAnimatedRef.current = true;
      return;
    }

    indicator.style.transform = `translate3d(${left}px, ${top}px, 0)`;
    indicator.style.width = `${width}px`;
    indicator.style.height = `${height}px`;
    indicator.style.opacity = "1";
  }, []);

  React.useLayoutEffect(() => {
    updateIndicator();
    const list = listRef.current;
    if (!list) return;
    const animationFrame = requestAnimationFrame(updateIndicator);
    const observer = new MutationObserver(updateIndicator);
    observer.observe(list, {
      attributes: true,
      attributeFilter: ["data-active"],
      childList: true,
      subtree: true,
    });
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updateIndicator);
    resizeObserver?.observe(list);
    for (const child of Array.from(list.children)) {
      if (child instanceof HTMLElement) {
        resizeObserver?.observe(child);
      }
    }
    window.addEventListener("resize", updateIndicator);
    return () => {
      cancelAnimationFrame(animationFrame);
      observer.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateIndicator);
    };
  }, [updateIndicator]);

  return (
    <TabsPrimitive.List
      ref={listRef}
      data-slot="segmented-control-list"
      className={cn(
        "bg-surface-base relative inline-flex h-9 w-fit items-center justify-center rounded-lg p-1",
        className,
      )}
      {...props}
    >
      <div
        ref={indicatorRef}
        aria-hidden
        className="bg-surface-elevated gradient-raised border-border-hairline pointer-events-none absolute top-0 left-0 origin-top-left rounded-md border opacity-0 shadow-raised [transition:transform_320ms_cubic-bezier(0.32,0.72,0,1),width_320ms_cubic-bezier(0.32,0.72,0,1),height_320ms_cubic-bezier(0.32,0.72,0,1),opacity_180ms_ease-out] will-change-[transform,width,height]"
      />
      {children}
    </TabsPrimitive.List>
  );
}

function SegmentedControlTrigger({
  className,
  ...props
}: TabsPrimitive.Tab.Props) {
  return (
    <TabsPrimitive.Tab
      data-slot="segmented-control-trigger"
      // `transform-gpu` is load-bearing, not an optimization: the SegmentedControlList
      // indicator is permanently composited (will-change + translate3d), and
      // WebKit sorts a composited layer above non-composited siblings even
      // when z-index says otherwise (labels vanished behind the indicator on
      // iPad, worst inside sticky headers/scroll panes). Promoting the
      // trigger keeps layer order symmetric with the indicator — see
      // GUIDELINES.md § Layer Promotion Must Be Symmetric.
      className={cn(
        "relative z-10 transform-gpu focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring text-foreground inline-flex h-full flex-1 items-center justify-center gap-2 rounded-md px-2 py-1 text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 cursor-pointer",
        className,
      )}
      {...props}
    />
  );
}

function SegmentedControlContent({
  className,
  ...props
}: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel
      data-slot="segmented-control-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  );
}

export {
  SegmentedControl,
  SegmentedControlContent,
  SegmentedControlList,
  SegmentedControlTrigger,
};
